import React, { useState, useEffect, useRef, Component, createRef } from "react";
import {useNavigate} from "react-router-dom";
import axios from 'axios';
import Verse from "../components/Verse"
import Comment from "../components/Comment";
import { getCurrentCCBibleStudyLink } from '../utils/ccbiblestudyLinks';
import Gleaner from "../components/Gleaner";
import Outline from "../components/Outline";
import ShortMessage from "../components/ShortMessage";
import Testimony from "../components/Testimony";
import YTImage from "../components/YTImage";
import YTModal from "../components/YTModal";
import SearchPlaylistPopover from "../components/SearchPlaylistPopover";
import CrossReference from "../components/CrossReference";
import Prayer from "../components/Prayer";
import GotQuestions from "../components/GotQuestions";
import TranslateInput, {TranslateLanguage} from '../components/KeywordTranslator';
import VoiceSearchBox from '../components/VoiceSearchBox';
import Playlist from "../components/Playlist";
import ShareList from "../components/ShareList";
import DailyWidget from "../components/DailyWidget";
import Footer from '../components/Footer';
import TopRightMenu from "../components/TopRightMenu"; 
import LanguageToggle from "../components/LanguageToggle";
import { getShareResourcesWithParticipantsDefault } from './readingPlanPreferences';
import FacetView from '../components/FacetView';
import logosmall from '../assets/CTBLogoCircle-Small.png';
import logomedium from '../assets/CTBLogoCircle-Medium.png';
import logo from '../assets/CTBLogo.png';
import micIcon from '../assets/CTB-Mic-P.png';
import bibleIcon from '../assets/bible.png';
import AppSearchAPIConnector from "@elastic/search-ui-app-search-connector";
import {
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutHeader,
  EuiFlyoutFooter,
  EuiText,
  EuiTitle,
  EuiSpacer,
  EuiPanel,
  EuiButtonEmpty,
  EuiSelect,
  EuiButton,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiCheckbox,
  EuiToolTip,
  EuiFlexItem,
  EuiBadge,
  EuiCallOut,
  EuiPageTemplate,
  EuiEmptyPrompt,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiIcon,
  EuiLink,
  EuiHeader,
  EuiHeaderSection,
  EuiHeaderSectionItem,
  EuiOverlayMask
} from '@elastic/eui';
import {
  ErrorBoundary,
  Facet,
  SearchProvider,
  SearchBox,
  Results,
  PagingInfo,
  ResultsPerPage,
  Paging,
  Sorting,
  WithSearch
} from "@elastic/react-search-ui";
import { Paging as PagingView } from "@elastic/react-search-ui-views";
import { Layout } from "@elastic/react-search-ui-views";
import { MultiCheckboxFacet } from "@elastic/react-search-ui-views";
import '@elastic/eui/dist/eui_theme_light.css'; // EUI 主题样式
import '@elastic/react-search-ui-views/lib/styles/styles.css'; // React Search UI Views 样式
import '../style.css';
import dayjs from "dayjs";
import { css } from '@emotion/react';
import {
  buildAutocompleteQueryConfig,
  buildFacetConfigFromConfig,
  buildSearchOptionsFromConfig,
  buildSortOptionsFromConfig,
  getConfig,
  getFacetFields
} from "../config/config-helper";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import facetView from "../components/FacetView";
import LoadUserSettings, {SavePlayListItem, SaveShareListItem, SaveUserSettings} from '../components/UserServices';
import PlaylistModal from "../components/PlaylistModal"
import FavoriteModal from "../components/FavoriteModal"
import { jwtDecode } from "jwt-decode"; 
import BibleSelectorModal from './BibleSelectorModal';
import ChatBotModal from './ChatBotModal';
import { Capacitor } from '@capacitor/core';
import { useSpeechBridge } from '../components/useSpeechBridge';
import { LoginRequiredModal } from './LoginRequiredModal';
import ScriptureReferenceLinks from './ScriptureReferenceLinks';
import InlineCrossReferences from './InlineCrossReferences';
import SearchResultEditor from './SearchResultEditor';
import {
  buildElasticSearchTerm,
  buildRelevanceSearchOptions,
  getScriptureContentQuery,
  isExactScriptureVerseResult,
  rankScriptureContentResults,
  rerankScriptureSearchResponse,
} from '../utils/searchRelevance';
import { parseReadingPlanReference } from '../utils/scriptureReferences';

const { hostIdentifier, searchKey, endpointBase, engineName } = getConfig();
const initialSearchParams = typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search)
  : new URLSearchParams();
const initialSearchTerm = initialSearchParams.get('q') || '';
const initialReadingPlanId = Number(initialSearchParams.get('readingPlanId')) || null;
const initialReadingPlanDayId = Number(initialSearchParams.get('readingPlanDayId')) || null;
class RelevanceAwareAppSearchConnector extends AppSearchAPIConnector {
  async onSearch(state, queryConfig) {
    const response = await super.onSearch({
      ...state,
      searchTerm: buildElasticSearchTerm(state.searchTerm),
    }, {
      ...queryConfig,
      ...buildRelevanceSearchOptions(state.searchTerm),
    });
    const rankedResponse = {
      ...rerankScriptureSearchResponse(response, state.searchTerm),
      featuredScripture: null,
      relatedScriptureResults: [],
    };
    const reference = parseReadingPlanReference(state.searchTerm);
    const isSingleVerse = reference
      && reference.verseStart !== null
      && reference.verseStart === reference.verseEnd
      && reference.chapter === reference.chapterEnd;
    const currentPage = Number(state.current || queryConfig.current || 1);
    if (!isSingleVerse || currentPage !== 1) {
      return rankedResponse;
    }

    let exactVerse = rankedResponse.results.find(result =>
      isExactScriptureVerseResult(result, reference));
    if (!exactVerse) {
      // An explicit sort can keep the exact verse outside the first page.
      // Fetch it separately without dropping an ordinary search result.
      try {
        const exactResponse = await super.onSearch({
          ...state,
          searchTerm: '',
          current: 1,
          resultsPerPage: 1,
          filters: [
            ...(state.filters || []),
            { field: 'doctype', values: ['經文'], type: 'all' },
            { field: 'chapter_code_name', values: [reference.book], type: 'all' },
            { field: 'chapter_number', values: [String(reference.chapter)], type: 'all' },
            { field: 'verse_number', values: [String(reference.verseStart)], type: 'all' },
          ],
        }, {
          ...queryConfig,
          current: 1,
          resultsPerPage: 1,
          facets: {},
          // No facets are requested, so do not retain the main query's
          // disjunctive facet fields (the connector expects both together).
          disjunctiveFacets: [],
        });
        exactVerse = exactResponse.results.find(result =>
          isExactScriptureVerseResult(result, reference));
        rankedResponse.featuredScripture = exactVerse || null;
      } catch {
        return rankedResponse;
      }
    }

    const verseText = getScriptureContentQuery(exactVerse);
    const scriptureOnlyFilter = (state.filters || []).some(filter =>
      filter.field === 'doctype'
      && filter.values?.length > 0
      && filter.values.every(value => value === '經文'));
    if (verseText.length < 6 || scriptureOnlyFilter) return rankedResponse;

    try {
      const relatedResponse = await super.onSearch({
        ...state,
        searchTerm: verseText,
        current: 1,
        resultsPerPage: 12,
        sortField: undefined,
        sortDirection: undefined,
        sortList: [],
        filters: [
          ...(state.filters || []),
          { field: 'doctype', values: ['經文'], type: 'none' },
        ],
      }, {
        ...queryConfig,
        current: 1,
        resultsPerPage: 12,
        facets: {},
        disjunctiveFacets: [],
        sortField: undefined,
        sortDirection: undefined,
        sortList: [],
        search_fields: {
          title: { weight: 8 },
          description: { weight: 6 },
          prayer: { weight: 5 },
          verse: { weight: 4 },
          tag: { weight: 4 },
        },
        result_fields: {
          ...(queryConfig.result_fields || {}),
          book: { raw: {} },
          chapter: { raw: {} },
        },
      });
      rankedResponse.relatedScriptureResults = rankScriptureContentResults(
        relatedResponse.results, verseText, reference);
    } catch (error) {
      // The ordinary reference search remains available if the optional
      // content search fails.
      console.warn('Could not search related verse content:', error?.message || error);
    }
    return rankedResponse;
  }
}

const connector = new RelevanceAwareAppSearchConnector({
  searchKey,
  engineName,
  hostIdentifier,
  endpointBase
});
const config = {
  searchQuery: {
    disjunctiveFacets: ["doctype","chapter_name","verse","versenumber","publisher","tempo"],
    facets: buildFacetConfigFromConfig(),
    ...buildSearchOptionsFromConfig()
  },
  autocompleteQuery: buildAutocompleteQueryConfig(),
  apiConnector: connector,
  alwaysSearchOnInitialLoad: Boolean(initialSearchTerm),
  initialState: {resultsPerPage: 20, searchTerm: initialSearchTerm}
};
const searchResultValue = (result, field) => {
  const value = result?.[field]?.raw ?? result?.[field]?.snippet ?? '';
  return Array.isArray(value) ? value.join(', ') : String(value || '');
};
const visibleTextLength = value => String(value || '').replace(/<[^>]*>/g, '').trim().length;
const plainSearchSnippet = value => String(value || '').replace(/<[^>]*>/g, '');
const readingPlanResultKey = result => searchResultValue(result, 'id')
  || searchResultValue(result, 'link')
  || searchResultValue(result, 'title');
const readingPlanSourceName = (result, doctype) => {
  if (doctype === '經文') {
    const book = searchResultValue(result, 'chapter_name') || searchResultValue(result, 'chapter_code_name');
    const chapter = searchResultValue(result, 'chapter_number');
    const verse = searchResultValue(result, 'verse_number') || searchResultValue(result, 'chapter_verse_number');
    if (book && chapter && verse) return `${book} ${chapter}:${verse}`;
    if (book && chapter) return `${book} ${chapter}`;
  }
  return searchResultValue(result, 'publisher') || searchResultValue(result, 'origin') || doctype;
};
const readingPlanResourceType = doctype => ({
  '經文': 'scripture',
  '詩歌': 'song',
  '信息': 'sermon',
  '網路': 'web',
  '禱告': 'prayer',
  '註解': 'commentary',
  '拾穗': 'commentary',
  '綱目': 'outline',
  '例證': 'illustration',
}[doctype] || 'article');

export default function Search() {
  const navigate = useNavigate();
  const [isFlyoutVisible, setIsFlyoutVisible] = useState({currentState: false, book: "", chapter: "", bookname: "", versenumber: "",  verse_id: "", link: "", doctype: ""});
  const selectedFlyoutVerseRef = useRef(null);
  const flyoutSearchActionsRef = useRef(null);
  const closeFlyout = () => {
    setIsFlyoutVisible({currentState: false, book: "", chapter:"", bookname: "", versenumber: "", verse_id: "", link: "", doctype: ""});
    selectedFlyoutVerseRef.current = null;
  };
  const [exactSearch, setExactSearch] = useState(false);
  const [sortViewCnt, setSortViewCnt] = useState(false);
  const [sortDateCnt, setSortDate] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const [YTVideo, setYTVideo] = useState({isActive: false, videoURL: "", id:"",title:""});
  const [isVoiceSearch, setIsVoiceSearch] = useState(false);
  const [itemSaved, setItemSaved] = useState(false);
  const currentURL = window.location.origin;
  var currentBrowserLanguage = (navigator.language || navigator.userLanguage) == "zh-CN" ? "S" : "T"
  var userLanguage = (localStorage.userLanguage) ? localStorage.userLanguage : currentBrowserLanguage;
  localStorage.userLanguage = userLanguage;
  const [language, setLanguage] = useState(userLanguage);
  const [showPlaylistModal, setShowModal] = useState(false);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [deletedResultIds, setDeletedResultIds] = useState([]);
  const [modalProps, setModalProps] = useState({
    userId: '',
    songId: '',
    videoURL: '',
    type: '',
    engineId: '',
    title: '',
    label: '',
    playlisttype: ''
  });
  const [isModalVisible, setBibleModalVisible] = useState(false);
  const [isModalHomeVisible, setBibleModalHomeVisible] = useState(false);
  const [isChatBotVisible, setIsChatBotVisible] = useState(false);
  const inputRef = useRef(null); // 引用輸入框
  const inputHomeRef = useRef(null); // 引用輸入框
  const searchPageTopRef = useRef(null);

  useEffect(() => {
    if (!isFlyoutVisible.currentState || isFlyoutVisible.doctype !== '經文') return;
    const match = String(isFlyoutVisible.versenumber || '').match(/\d+/);
    selectedFlyoutVerseRef.current = {
      book: isFlyoutVisible.book,
      chapter: isFlyoutVisible.chapter,
      verse: match ? Number(match[0]) : 1,
    };
  }, [
    isFlyoutVisible.currentState,
    isFlyoutVisible.doctype,
    isFlyoutVisible.book,
    isFlyoutVisible.chapter,
    isFlyoutVisible.versenumber,
  ]);

  const searchSelectedFlyoutVerse = () => {
    const selectedFlyoutVerse = selectedFlyoutVerseRef.current;
    if (!selectedFlyoutVerse) return;
    const term = `${selectedFlyoutVerse.book}${selectedFlyoutVerse.chapter}:${selectedFlyoutVerse.verse}`;
    const translatedTerm = TranslateInput(term);
    if (inputRef.current) inputRef.current.value = term;
    flyoutSearchActionsRef.current?.removeFilter('doctype');
    flyoutSearchActionsRef.current?.setSearchTerm(translatedTerm);
    closeFlyout();
    scrollToSearchPageTop();
  };
  const token = sessionStorage.getItem("token");  // 从 localStorage 中获取 token
  const readingPlanContextActive = Boolean(initialReadingPlanId && initialReadingPlanDayId);
  const [readingPlanAddingId, setReadingPlanAddingId] = useState('');
  const [readingPlanAddedIds, setReadingPlanAddedIds] = useState([]);
  const [readingPlanMessage, setReadingPlanMessage] = useState(null);
  const [expandedResultContent, setExpandedResultContent] = useState([]);
  const toggleResultContent = resultKey => {
    setExpandedResultContent(current => current.includes(resultKey)
      ? current.filter(key => key !== resultKey)
      : [...current, resultKey]);
  };
  const renderExpandableContent = (resultKey, html, label = '', threshold = 180, isLyrics = false) => {
    if (!html) return null;
    const isExpanded = expandedResultContent.includes(resultKey);
    // Line breaks and narrow screens can overflow the lyric clamp even when the text is short.
    const canExpand = isLyrics || visibleTextLength(html) > threshold;
    return (
      <div className={`search-result-expandable-wrap${isLyrics ? ' song-lyrics-wrap' : ''}`}>
        <div className={`search-result-expandable${isExpanded ? ' is-expanded' : ' is-collapsed'}`}>
          {label && <strong>{TranslateLanguage(language, label)}</strong>}
          <span dangerouslySetInnerHTML={{__html: TranslateLanguage(language, html)}} />
        </div>
        {canExpand && (
          <EuiButtonEmpty
            size="xs"
            flush="left"
            iconType={isExpanded ? 'arrowUp' : 'arrowDown'}
            onClick={() => toggleResultContent(resultKey)}
          >
            {TranslateLanguage(language, isExpanded ? '收起內容' : '展開內容')}
          </EuiButtonEmpty>
        )}
      </div>
    );
  };
  const readingPlanReturnPath = readingPlanContextActive
    ? `/reading-plans?planId=${initialReadingPlanId}&dayId=${initialReadingPlanDayId}`
    : '/reading-plans';

  const handleAddToReadingPlan = async result => {
    if (!token) {
      setShowLoginModal(true);
      return;
    }
    const resultKey = readingPlanResultKey(result);
    const doctype = searchResultValue(result, 'doctype');
    const title = searchResultValue(result, 'title') || '相關內容';
    const link = searchResultValue(result, 'link') || `/?q=${encodeURIComponent(title)}`;
    setReadingPlanAddingId(resultKey);
    setReadingPlanMessage(null);
    try {
      await axios.post(`/api/readingplans/${initialReadingPlanId}/days/${initialReadingPlanDayId}/resources`, {
        resourceType: readingPlanResourceType(doctype),
        title,
        description: searchResultValue(result, 'description') || searchResultValue(result, 'source'),
        link,
        sourceName: readingPlanSourceName(result, doctype),
        resourceId: searchResultValue(result, 'id'),
        engineId: engineName,
        isSharedWithParticipants: getShareResourcesWithParticipantsDefault(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReadingPlanAddedIds(previous => [...previous, resultKey]);
      setReadingPlanMessage({ color: 'success', text: '已加入當日相關內容。' });
    } catch (error) {
      if (error?.response?.status === 409) {
        setReadingPlanAddedIds(previous => [...previous, resultKey]);
        setReadingPlanMessage({ color: 'warning', text: '這項內容已經加入當日安排。' });
      } else {
        setReadingPlanMessage({
          color: 'danger',
          text: error?.response?.data?.message || '加入失敗，請稍後再試。',
        });
      }
    } finally {
      setReadingPlanAddingId('');
    }
  };


  const openBibleSelectModal = () => setBibleModalVisible(true);
  const closeBibleSelectModal = () => setBibleModalVisible(false);
  const openBibleSelectHomeModal = () => setBibleModalHomeVisible(true);
  const closeBibleSelectHomeModal = () => setBibleModalHomeVisible(false);
  const showChatBot = () => setIsChatBotVisible(true);
  const closeChatBot = () => setIsChatBotVisible(false);
  
  const handleSelectVerse = (verse, setSearchTerm, setFilter, removeFilter, exactSearch, TranslateInput) => {
    // 设置输入框的值
    inputRef.current.value = verse;
    // 设置完输入框的值后立即触发搜索
    handleSearch(setSearchTerm, setFilter, removeFilter, exactSearch, TranslateInput);
  };

  const handleSelectVerseHome = (verse, setSearchTerm, setFilter, removeFilter, exactSearch, TranslateInput) => {
    // 设置输入框的值
    if (inputHomeRef.current) {
      inputHomeRef.current.value = verse;  // 直接通过 ref 修改输入框的值
      setSearchTerm(TranslateInput(verse));  // 使用获取的值进行搜索
    }
  };

  let userRole = "";
  if (token) {
      const decodedToken = jwtDecode(token);  // 解码 token
      // console.log(decodedToken);  // 检查 token 中的内容，包括角色信息
      userRole = decodedToken.role;  // 获取角色信息
  }

  // 在应用加载时调用这个函数来检查用户是否已登录
  if (checkTokenValidity()) {
    // 用户免登录，自动恢复会话
    const user = JSON.parse(localStorage.getItem('user'));
    // 继续使用用户信息
    // console.log('User:', user);
  } else {
    // 需要重新登录
    // console.log('请重新登录');
  }

    // 检查是否有 JWT 令牌
  function checkTokenValidity() {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (token) {
      // 这里你可以检查 JWT 令牌是否仍然有效，比如解码并检查过期时间
      const tokenExpiry = JSON.parse(atob(token.split('.')[1])).exp; // 获取 token 的过期时间
      const currentTime = Math.floor(Date.now() / 1000);

      if (tokenExpiry > currentTime) {
        // Token 还有效，继续保持登录状态
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', user);
        return true;
      } else {
        // Token 已经过期，清除本地存储的 token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return false;
      }
    } else {
      return false; // 没有 token
    }
  }

  // 将小数转换为 hh:mm:ss 格式
  function convertDecimalToTime(decimal) {
      const totalSeconds = Math.round(decimal * 3600 / 2.5); // 将小数转换为总秒数
      const hours = Math.floor(totalSeconds / 3600);          // 计算小时
      const remainingSeconds = totalSeconds % 3600;
      const minutes = Math.floor(remainingSeconds / 60);      // 计算分钟
      const seconds = remainingSeconds % 60;                  // 计算秒数

      // 如果小时为 0，只显示 mm:ss 格式
      if (hours === 0) {
          return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
      }

      // 否则显示完整的 hh:mm:ss 格式
      return `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  function formatViewCountCompact(count) {
      if (count < 1000) return "<1k";
      if (count < 10000) return "1k-10k";
      if (count < 100000) return "10k-100k";
      if (count < 1000000) return "100k-1M";
      return ">1M";
  }

  // useEffect(() => {
  //     // 添加类名禁用滚动条
  //     document.documentElement.classList.add('no-scroll');

  //     // 当组件卸载时移除类名
  //     return () => {
  //        document.documentElement.classList.remove('no-scroll');
  //     };
  // }, []);
  useEffect(() => {
    const appViewport =
      document.querySelector('.appViewport');

    appViewport?.classList.add(
      'search-page-active'
    );

    return () => {
      appViewport?.classList.remove(
        'search-page-active'
      );
    };
  }, []);

  const handleAddplaylistClick = (r) => {
    const user = sessionStorage.getItem('user');
    if (user) {
      // 如果用户已登录，显示 PlaylistModal 组件
      setModalProps({
        userId: JSON.parse(user).id, // 假设 user 对象里有 id 字段
        songId: r.id.raw,
        videoURL: r.link.raw,
        type: r.doctype.raw, 
        engineId: r.id.raw, 
        title: r.title.raw,
        label: 'v',
        playlisttype: 'song'
      });
      setShowModal(true);
    } else {
      setShowLoginModal(true);
      // 用户未登录，执行原有的保存操作
      // SavePlayListItem("", 1, r.id.raw, r.link.raw, r.title.raw);
      // setIsFlyoutVisible({
      //   currentState: true,
      //   book: "",
      //   chapter: "",
      //   bookname: "",
      //   verse_id: "",
      //   link: "",
      //   doctype: "我的歌單"
      // });
    }
  };

  const handleAddradiolistClick = (r) => {
    const user = sessionStorage.getItem('user');
    if (user) {
      // 如果用户已登录，显示 PlaylistModal 组件
      setModalProps({
        userId: JSON.parse(user).id, // 假设 user 对象里有 id 字段
        songId: r.id.raw,
        videoURL: r.link.raw,
        type: r.doctype.raw, 
        engineId: r.id.raw, 
        title: r.title.raw,
        label: 'v',
        playlisttype: 'radio'
      });
      setShowModal(true);
    } else {
      return (
        <EuiText color="danger">
          {TranslateLanguage(language, "請登錄管理員操作")}
        </EuiText>
      );
    }
  };

  const handleAddfavoriteClick = (r,link,itemType) => {
    const user = sessionStorage.getItem('user');
    if (user) {
      // 如果用户已登录，显示 PlaylistModal 组件
      setModalProps({
        userId: JSON.parse(user).id, // 假设 user 对象里有 id 字段
        songId: r.id.raw,
        videoURL: link,
        type: r.doctype.raw, 
        engineId: r.id.raw, 
        title: r.title.raw,
        label: itemType,
        resource: r,
        playlisttype: 'favorite'
    });
      setShowFavoriteModal(true);
    } else {
      setShowLoginModal(true);
      // 用户未登录，执行原有的保存操作
      // SaveShareListItem("",1,r.id.raw, link , r.title.raw, itemType, r.doctype.raw );
      // setIsFlyoutVisible({currentState: true, book: "", chapter: "", bookname: "", verse_id: "", link: "", doctype: "我的收藏"});
    }
  };

  const handlePlayPlaylistClick = () => {
      // 检查 sessionStorage 中是否存在用户信息
      const user = sessionStorage.getItem('user');

      if (user) {
          // 如果用户已登录，跳转到 playerpage
          navigate('/playerpage');
      } else {
          // 如果用户未登录，跳转到 player 页面
          //navigate('/player');
          setShowLoginModal(true);
          
      }
  };

  const handleEnterFavorlistClick = () => {
    // 检查 sessionStorage 中是否存在用户信息
    const user = sessionStorage.getItem('user');

    if (user) {
        // 如果用户已登录，跳转到 favoritepage
        navigate('/favoritepage');
    } else {
        // 如果用户未登录，跳转到 sharepage
        //navigate('/sharepage');
        setShowLoginModal(true);
    }
};

    const handleSearch = (setSearchTerm, setFilter, removeFilter, exactSearch, TranslateInput) => {
      const term = inputRef.current.value;
      let newValue = exactSearch ? `"${term.split('"').join('')}"` : term.split('"').join('');
      newValue = TranslateInput(newValue);

      if (newValue.startsWith("#")) {
        setSearchTerm(newValue);
        setFilter("tag", newValue.replace("#", ""), "any");
      } else {
        setSearchTerm(newValue);
        removeFilter("doctype");
      }
      inputRef.current.value = term; // 确保搜索后保留输入框的内容
    };

  const formatDateYMD = (input) => {
    if (!input) return "";
    const d = dayjs(input);
    return d.isValid() ? d.format("YYYY-MM-DD") : "";
  };

  const onChangeLanguage  = () => {
       language == "T"? setLanguage("S"):setLanguage("T");
       language == "T"? localStorage.userLanguage = "S" : localStorage.userLanguage = "T"
       window.scrollTo(0, 0);
      };

  const scrollToSearchPageTop = () => {
    const scroll = () => {
      if (searchPageTopRef.current) {
        searchPageTopRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        return;
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(scroll));
    } else {
      window.setTimeout(scroll, 0);
    }
  };

  // const {
  //   transcript,
  //   listening,
  //   resetTranscript,
  //   browserSupportsSpeechRecognition
  // } = useSpeechRecognition();

  //add on 9/6/2025
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    startListening,
    stopListening,
  } = useSpeechBridge();

  let flyout;
  // if (isFlyoutVisible.currentState) {
  //   flyout = (
  //     <EuiFlyout
  //       ownFocus
  //       onClose={() => setIsFlyoutVisible({currentState: false, book: "", chapter:"", bookname: "", versenumber: "",  verse_id: "", link: "", doctype: ""})}
  //       aria-labelledby="flyoutTitle">
  //         {isFlyoutVisible.doctype == "經文" && <Verse book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber} language={language} />}
  //         {isFlyoutVisible.doctype == "註解" && <Comment book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber}  language={language} />}
  //         {isFlyoutVisible.doctype == "拾穗" && <Gleaner book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber}  language={language} />}
  //         {isFlyoutVisible.doctype == "例證" && <Testimony book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber} language={language}  />}
  //         {isFlyoutVisible.doctype == "綱目" && <Outline book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber}  language={language} />}
  //         {isFlyoutVisible.doctype == "短篇信息" && <ShortMessage book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber}  language={language} />}
  //         {isFlyoutVisible.doctype == "串珠" && <CrossReference book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber} verse_id ={isFlyoutVisible.verse_id} language={language} />}
  //         {isFlyoutVisible.doctype == "禱告" && <Prayer book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber} verse_id ={isFlyoutVisible.verse_id}  language={language} />}
  //         {isFlyoutVisible.doctype == "信仰問答" && <GotQuestions book={isFlyoutVisible.book} chapter={isFlyoutVisible.chapter} bookname={isFlyoutVisible.bookname} versenumber={isFlyoutVisible.versenumber} verse_id ={isFlyoutVisible.verse_id}  language={language} />}
  //         {isFlyoutVisible.doctype == "我的歌單" && <Playlist language={language}/>}
  //         {isFlyoutVisible.doctype == "我的收藏" && <ShareList language={language}/>}
  //     </EuiFlyout>
  //   );
  // }

  if (isFlyoutVisible.currentState) {
    flyout = (
      <EuiFlyout
        ownFocus
        size="m"
        className="search-detail-flyout"
        onClose={closeFlyout}
        aria-labelledby="flyoutTitle"
      >
        <EuiFlyoutBody
          className="search-detail-flyout-body"
        >
            <div
              className="search-detail-content"
              onWheel={(event) => {
                event.stopPropagation();
              }}
            >
            {isFlyoutVisible.doctype === '經文' && (
              <Verse
                key={[isFlyoutVisible.book, isFlyoutVisible.chapter, isFlyoutVisible.versenumber].join('-')}
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                language={language}
                useFooterAudioControls
                footerAudioTargetId="search-scripture-footer-audio"
                footerNoteTargetId="search-scripture-footer-note"
                onVerseChange={selection => {
                  selectedFlyoutVerseRef.current = selection;
                }}
              />
            )}

            {isFlyoutVisible.doctype === '註解' && (
              <Comment
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '拾穗' && (
              <Gleaner
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '例證' && (
              <Testimony
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '綱目' && (
              <Outline
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '短篇信息' && (
              <ShortMessage
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '串珠' && (
              <CrossReference
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                verse_id={isFlyoutVisible.verse_id}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '禱告' && (
              <Prayer
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                verse_id={isFlyoutVisible.verse_id}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '信仰問答' && (
              <GotQuestions
                book={isFlyoutVisible.book}
                chapter={isFlyoutVisible.chapter}
                bookname={isFlyoutVisible.bookname}
                versenumber={isFlyoutVisible.versenumber}
                verse_id={isFlyoutVisible.verse_id}
                language={language}
              />
            )}

            {isFlyoutVisible.doctype === '我的歌單' && (
              <Playlist language={language} />
            )}

            {isFlyoutVisible.doctype === '我的收藏' && (
              <ShareList language={language} />
            )}
          </div>
        </EuiFlyoutBody>

        <EuiFlyoutFooter
          className="search-detail-flyout-footer"
        >
          <EuiFlexGroup
            justifyContent="flexEnd"
            alignItems="center"
            gutterSize="s"
            responsive={false}
            wrap
          >
            {isFlyoutVisible.doctype === '經文' && (
              <EuiFlexItem className="scripture-footer-audio-slot">
                <div id="search-scripture-footer-audio" />
              </EuiFlexItem>
            )}
            {isFlyoutVisible.doctype === '經文' && (
              <EuiFlexItem grow={false} className="scripture-footer-action">
                <div id="search-scripture-footer-note" />
              </EuiFlexItem>
            )}
            {isFlyoutVisible.doctype === '經文' && (
              <EuiFlexItem grow={false} className="scripture-footer-action">
                <EuiButton
                  onClick={searchSelectedFlyoutVerse}
                >
                  {TranslateLanguage(language, '搜尋')}
                </EuiButton>
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false} className={isFlyoutVisible.doctype === '經文' ? 'scripture-footer-action' : ''}>
              <EuiButton
                onClick={closeFlyout}
                fill
              >
                {TranslateLanguage(
                  language,
                  '關閉'
                )}
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlyoutFooter>
      </EuiFlyout>
    );
  }

  let ytPreview;
  if (YTVideo.isActive){
    ytPreview = (
      <EuiModal 
        style={{ 
          width: 800,
          height: '85%',
          minHeight: '50vh',
          margin: 'auto'
        }} 
        onClose={()=>{setYTVideo({isActive: false, videoURL: "", id: "", title: ""}); setItemSaved(false)}}>
          <EuiModalBody style={{ paddingTop: '30px' }}>
            <YTModal videoURL={YTVideo.videoURL} />
          </EuiModalBody>
          <EuiModalFooter>
          <EuiFlexGroup>
            <EuiFlexItem></EuiFlexItem>
            {itemSaved ? (<EuiFlexItem grow={false}>
              <EuiIcon type="check" size="l"  paddingSize="none"></EuiIcon>
            </EuiFlexItem>)
            : null}
            {/* <EuiFlexItem grow={false} gutterSize="s">
              <EuiButtonEmpty iconType="heart" size="xs" color="primary" onClick={()=>{
                              SavePlayListItem("",1,YTVideo.id, YTVideo.videoURL, YTVideo.title);
                              setItemSaved(true);
                              }}>
                              {TranslateLanguage(language,"加入我的歌單")}
              </EuiButtonEmpty>
            </EuiFlexItem> */}
            <EuiFlexItem grow={false}>
              <SearchPlaylistPopover
                userId={sessionStorage.getItem('user') ? JSON.parse(sessionStorage.getItem('user')).id : ''}
                videoURL={YTVideo.videoURL}
                title={YTVideo.title}
                type="詩歌"
                engineId={YTVideo.id}
                label="v"
                onLoginRequired={() => setShowLoginModal(true)}
              />
            </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton color="primary" onClick={()=>{setYTVideo({isActive: false, videoURL:"", id: "", title: ""}); setItemSaved(false);}}>{TranslateLanguage(localStorage.userLanguage, "關閉")}</EuiButton>
              </EuiFlexItem>
          </EuiFlexGroup>  
          </EuiModalFooter>
      </EuiModal>
    );
  }

  return (
    <SearchProvider config={config}>
      <div className={`search-page-shell${Capacitor.isNativePlatform() ? ' is-native-app' : ''}`}>
      <div className="appHeaderSticky">
        <EuiHeader className="no-shadow-header">
          <EuiHeaderSection>
            {/* 左侧：Logo / 搜索等 */}
          </EuiHeaderSection>
          <EuiHeaderSection side="right">
            {/* 右侧：你的按钮 */}
            <WithSearch
              mapContextToProps={({ wasSearched /* 需要的话还可取 resultSearchTerm 等 */ }) => ({
                wasSearched,
              })}
            >
              {({ wasSearched }) => (
                <>
                  {wasSearched && (
                    <EuiHeaderSectionItem>
                      <EuiButtonEmpty
                        size="s"
                        iconType="home"
                        onClick={() => (window.location.href = currentURL)}  // ✅ 点击返回起始页
                        color="text"
                        iconSide="left"
                        style={{
                          fontWeight: 500,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          height: '32px',
                          padding: '0 8px',
                        }}
                      >
                        {TranslateLanguage(localStorage.userLanguage, '首頁')}
                      </EuiButtonEmpty>
                    </EuiHeaderSectionItem>
                  )}
                </>
              )}
            </WithSearch>
            <EuiHeaderSectionItem>
              <LanguageToggle />
            </EuiHeaderSectionItem>
            <EuiHeaderSectionItem>
              <TopRightMenu withLanguage="1" />
            </EuiHeaderSectionItem>
          </EuiHeaderSection>
        </EuiHeader>
      </div>
      <WithSearch 
        mapContextToProps={({ resultSearchTerm, setSearchTerm, results, featuredScripture, relatedScriptureResults, setFilter, removeFilter, setSort, wasSearched }) => ({
          resultSearchTerm,
          setSearchTerm,
          results,
          featuredScripture,
          relatedScriptureResults,
          setFilter,
          removeFilter,
          setSort,
          wasSearched
        })} 
      >
        {({ resultSearchTerm, setSearchTerm, results, featuredScripture, relatedScriptureResults, setFilter, removeFilter, setSort, wasSearched }) => {

            flyoutSearchActionsRef.current = { setSearchTerm, removeFilter };

            // modal handling

            // const closeModal = () => {
            //   setIsVoiceSearch(false);
            //   (transcript) && setSearchTerm(TranslateInput(transcript)) ;
            //   SpeechRecognition.stopListening()
            // }
            // const cancelModal = () => {
            //   setIsVoiceSearch(false);
            //   SpeechRecognition.stopListening()
            // }
            // const showModal = () => {
            //   setIsVoiceSearch(true);
            //   SpeechRecognition.startListening({ language: 'cmn-Hant-TW' });
            // }

            const showModal = async () => {
              setIsVoiceSearch(true);
              resetTranscript();                           // 清空上次内容
              try {
                await startListening();
              } catch (e) {
                // e.message 可能是 'perm-not-granted:denied' 或 'web-speech-not-available'
                setIsVoiceSearch(false);
                showToast('請允許麥克風權限，或改用鍵盤輸入');
              }
            };

            const closeModal = async () => {
              setIsVoiceSearch(false);
              if (transcript?.trim()) {
                setSearchTerm(TranslateInput(transcript)); // 设置搜索词（你现有逻辑）
              }
              try { await stopListening(); } catch (e) { console.error(e); }
            };

            const cancelModal = async () => {
              setIsVoiceSearch(false);
              try { await stopListening(); } catch (e) { console.error(e); }
            };

            const handleSearchFromVoice = (text) => {
              const normalized = TranslateInput(text); // 你的轉換
              setSearchTerm(normalized);               // 寫回整頁的搜尋詞
            };

            // end of modal handleing

            const showYTPreview = (id, link, title) => {
              setYTVideo({isActive: true, videoURL: link, id: id, title: title});
            }

            //Misc.

            function getFilterValueDisplay(filterValue) {
              if (filterValue === undefined || filterValue === null) return "";
              if (filterValue.hasOwnProperty("name")) return filterValue.name;
              return String(filterValue);
            }

          //handle things need to update DOM after load
            const lazyLoading = () =>{
              setTimeout(function () {
                var element = document.getElementsByClassName("sui-layout-sidebar-toggle");
                if(element.length != 0){
                  element[0].innerHTML = TranslateLanguage(language,"選擇更多搜尋條件");
                  element[1].innerHTML = TranslateLanguage(language,"回到搜尋結果");
                }
            }, 0);
          }

          const visibleResults = (results || []).filter(r => !deletedResultIds.includes(r.id.raw));
          const currentReference = parseReadingPlanReference(resultSearchTerm);
          const exactResult = visibleResults.find(r => isExactScriptureVerseResult(r, currentReference));
          const orderedResults = [];
          const seenResultIds = new Set();
          const addResult = (result, flags = {}) => {
            const id = result?.id?.raw;
            if (!id || seenResultIds.has(id) || deletedResultIds.includes(id)) return;
            seenResultIds.add(id);
            orderedResults.push({ ...result, ...flags });
          };
          addResult(featuredScripture, { isFeaturedScripture: true });
          addResult(exactResult);
          (relatedScriptureResults || []).forEach(r => addResult(r, { isContentRelated: true }));
          visibleResults.forEach(r => addResult(r));

          return (
            // <div className='App'>
              <div
                ref={searchPageTopRef}
                className={[
                  'App',
                  'search-page-content',
                  wasSearched
                    ? 'search-page-content--results'
                    : 'search-page-content--home'
                ].join(' ')}
              >
              {lazyLoading()}
              {isVoiceSearch ? (
                <VoiceSearchBox
                  listening={listening}
                  transcript={transcript}
                  closeModal={closeModal}
                  cancelModal={cancelModal}
                />
                  // <VoiceSearchBox
                  //   initialText={transcript}             // 或把現有 transcript 傳進去
                  //   onSearch={handleSearchFromVoice}
                  //   onClose={() => setIsVoiceSearch(false)}
                  // />
              ) : null}

              {wasSearched ? (
              <ErrorBoundary>
                <Layout
                  header={  <EuiFlexGroup
                              className="header-bar"
                              gutterSize="s"               // ⬅️ 减小内间距
                              alignItems="center"
                              responsive={false}
                            >
                            <EuiFlexItem grow={false} className="header-logo">
                            <span align="center"><a href={currentURL}>
                              <picture>
                                <source media="(min-width:1280px)" srcSet={logosmall} />
                                <source media="(min-width:768px)" srcSet={logosmall} />
                                <img src={logomedium}  alt="Check The Bible" />
                              </picture></a></span>
                            </EuiFlexItem>
                            {/* <EuiFlexItem  justifyContent="left"> */}
                            <EuiFlexItem className="search-header-controls" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                            <SearchBox autocompleteSuggestions={false} className = "bar-result"
                                      onSubmit={(e) => {
                                        var term = document.getElementById("inputTerm").value;
                                        var newValue = exactSearch ? "\"" + term.split("\"").join("") + "\"" : term.split("\"").join("");
                                        newValue = TranslateInput(newValue);
                                        if(newValue.substring(0,1) == "#"){setSearchTerm(newValue); setFilter("tag",newValue.replace("#",""), "any");} 
                                        if(newValue.substring(0,1) != "#"){setSearchTerm(newValue); removeFilter("doctype");} 
                                      }} 
                                      inputView={({ getAutocomplete, getInputProps, getButtonProps }) => (
                                        <>
                                        <input
                                          {...getInputProps({
                                            placeholder: TranslateLanguage(language, "請輸入關鍵字。如馬太福音"),
                                            id: "inputTerm",
                                            className: "searchbar-result",
                                            ref: inputRef,
                                            defaultValue: initialSearchTerm,
                                            autoComplete: "off"
                                          })}
                                        />
                                          {/* {getAutocomplete()} */}
                                          {/* {(browserSupportsSpeechRecognition || Capacitor.isNativePlatform()) && <img onClick={()=>{showModal();}}  className="voice-result"  src={micIcon} title={TranslateLanguage(language,"語音搜尋")} />} */}
                                          {browserSupportsSpeechRecognition && !Capacitor.isNativePlatform() && (
                                            <img
                                              onClick={() => showModal()}
                                              className="voice-result"
                                              src={micIcon}
                                              title={TranslateLanguage(language, "語音搜尋")}
                                            />
                                          )}
                                          <EuiButtonIcon iconType="search" className="searchbutton-result" onClick={()=>{
                                                var term = document.getElementById("inputTerm").value;
                                                var newValue = exactSearch ? "\"" + term.split("\"").join("") + "\"" : term.split("\"").join("");
                                                newValue = TranslateInput(newValue);
                                                if(newValue.substring(0,1) == "#"){setSearchTerm(newValue); setFilter("tag",newValue.replace("#",""), "any");} 
                                                if(newValue.substring(0,1) != "#"){setSearchTerm(newValue); removeFilter("doctype");} 
                                          }}aria-label={TranslateLanguage(language, "搜尋")}>
                                          </EuiButtonIcon>
                                        </>
                                      )}
                            />
                              <div className="search-header-shortcuts">
                              <button className="search-header-icon" aria-label={TranslateLanguage(language, '選擇經文')} onClick={openBibleSelectModal} style={{ cursor: 'pointer', border: 'none', background: 'none', padding: '8px'  }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <EuiIcon size="l" type={bibleIcon} />
                                </div>
                              </button>
                              <button className="search-header-icon" aria-label={TranslateLanguage(language, '聊天')} onClick={showChatBot} style={{ cursor: 'pointer', border: 'none', background: 'none', padding: '8px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <EuiIcon size="l" type="userAvatar" />
                                </div>
                              </button>
                              </div>
                            </EuiFlexItem>
                            {/* <EuiFlexGroup alignItems="center" responsive={false}>
                              <EuiFlexItem grow={false}><EuiButton color="text" iconType="search" className="searchbutton" onClick={()=>{setSearchTerm(TranslateInput((document.getElementById("inputTermHome").value)));}}>搜尋</EuiButton></EuiFlexItem>
                            </EuiFlexGroup> */}
                              {/* <button onClick={openBibleSelectModal}>選擇經文</button> */}
                          </EuiFlexGroup>
                          }
                  sideContent={
                    <div>
                      {wasSearched && (
                        <Sorting
                          label={"排序/Sort"}
                          sortOptions={buildSortOptionsFromConfig()} 
                        />
                      )}
                      {/* {getFacetFields().map(field => (
                        <Facet key={field} field={field} label={field} />
                      ))} */}
                      <EuiSpacer></EuiSpacer>
                      <EuiText size="xs" color="subdued">{TranslateLanguage(language,"搜尋方式")}</EuiText>
                      <EuiSpacer size="s"></EuiSpacer>
                      <EuiCheckbox  id="exclusivesearch"
                                    label="最佳符合"
                                    checked={exactSearch}
                                    onChange={(e) => {
                                      var newSearchTerm = document.getElementById("inputTerm").value;
                                      newSearchTerm = e.target.checked ?  "\"" + newSearchTerm + "\"" : newSearchTerm.split("\"").join("");
                                      newSearchTerm = TranslateInput(newSearchTerm);
                                      // setSearchTerm(newSearchTerm);
                                      if(newSearchTerm.substring(0,1) == "#"){setSearchTerm(newSearchTerm); setFilter("tag",newSearchTerm.replace("#",""), "any");} 
                                      if(newSearchTerm.substring(0,1) != "#"){setSearchTerm(newSearchTerm); removeFilter("doctype");}                                       
                                      setExactSearch(e.target.checked);
                                    }}
                      ></EuiCheckbox>
                      <EuiCheckbox  id="sortViewCount"
                                    label="按關注度排序 - 詩歌"
                                    checked={sortViewCnt}
                                    onChange={(e) => {
                                      var newSearchTerm = document.getElementById("inputTerm").value;
                                      newSearchTerm = TranslateInput(newSearchTerm);
                                      // setSearchTerm(newSearchTerm);
                                      if(!sortViewCnt && newSearchTerm.substring(0,1) == "#"){setSearchTerm(newSearchTerm); setFilter("tag",newSearchTerm.replace("#",""), "any"); setSort("viewct","desc");} 
                                      if(!sortViewCnt && newSearchTerm.substring(0,1) != "#"){setSearchTerm(newSearchTerm);  setSort("viewct","desc");}                                       
                                      if(sortViewCnt && newSearchTerm.substring(0,1) == "#"){setSearchTerm(newSearchTerm); setFilter("tag",newSearchTerm.replace("#",""), "any");setSort("","");} 
                                      if(sortViewCnt && newSearchTerm.substring(0,1) != "#"){setSearchTerm(newSearchTerm); setSort("","");}                                       
                                  
                                      setSortViewCnt(e.target.checked);
                                    }}
                       ></EuiCheckbox>
                      <EuiSpacer></EuiSpacer>
                      <Facet field="doctype" label="搜尋來源" className={language} view={facetView} filterType="any" show={8} />
                      <Facet field="testment" label="書別" className={language} view={facetView} filterType="any" />
                      <Facet field="chapter_name" label="篇章" className={language} view={facetView} filterType="any" />
                      <Facet field="category" label={TranslateLanguage(language,"分類")}  className={language} view={facetView} filterType="any" />
                      <Facet field="language" label="語言" className={language} view={facetView} filterType="any" />
                      <Facet field="publisher" label="發行者" className={language} view={facetView} filterType="any" />
                      <Facet field="tempo" label="歌速" className={language} view={facetView} filterType="any" />
                    </div>
                  }
                  bodyContent={
                    <>
                      {readingPlanContextActive && (
                        <>
                          <EuiCallOut
                            title={TranslateLanguage(language, '選擇要加入讀經計劃的內容')}
                            color={readingPlanMessage?.color || 'primary'}
                            iconType="calendar"
                          >
                            <p>{TranslateLanguage(language, '可使用左側分類瀏覽經文、網路、詩歌、信息等搜尋結果。')}</p>
                            {readingPlanMessage && <p>{TranslateLanguage(language, readingPlanMessage.text)}</p>}
                            <EuiButtonEmpty iconType="arrowLeft" onClick={() => navigate(readingPlanReturnPath)}>
                              {TranslateLanguage(language, '返回讀經計劃')}
                            </EuiButtonEmpty>
                          </EuiCallOut>
                          <EuiSpacer size="m" />
                        </>
                      )}
                      {orderedResults.map(r => (
                        <div key={r.id.raw}>
                          <EuiSpacer size="m" />
                          <EuiPanel className={r.doctype.raw === "信息" ? "sermon-search-card" : undefined}>
                            <div key={r.id.raw}> </div>
                            {r.isFeaturedScripture && <EuiBadge color="primary">{TranslateLanguage(language, '精確經文')}</EuiBadge>}
                            <div><EuiTitle className="search-result-title">
                              {r.isContentRelated
                                ? <span>{TranslateLanguage(language, plainSearchSnippet(r.title?.snippet || r.title?.raw))}</span>
                                : <span dangerouslySetInnerHTML={{__html: TranslateLanguage(language, r.title.snippet)}} />}
                            </EuiTitle></div>
                            <div>
                            {(r.hasOwnProperty('chapter_name')) && 
                              <EuiText><h4 className="search-result-scripture-reference"><span dangerouslySetInnerHTML={{__html: TranslateLanguage(language, r.chapter_name.snippet) + ' ' + r.chapter_verse_number.raw}} /></h4></EuiText>}

                            {(r.hasOwnProperty('publisher') && r.doctype.raw !== "信息") &&
                              <EuiText><h4 className="search-result-publisher"><span dangerouslySetInnerHTML={{__html: TranslateLanguage(language,r.publisher.snippet)}} /></h4></EuiText>}

                            {(((r.doctype.raw == "詩歌") ) && (r.hasOwnProperty('description'))) && 
                              <EuiFlexGroup gutterSize="s">
                                <EuiFlexItem grow={false}><EuiLink onClick={()=> showYTPreview(r.id.raw, r.link.raw, r.title.raw)}><YTImage link={r.link.raw} ></YTImage></EuiLink></EuiFlexItem>
                                <EuiFlexItem>
                                <EuiFlexGroup direction="column" gutterSize="s">
                                  <EuiFlexItem>
                                    {renderExpandableContent(`song:${r.id.raw}`, r.description.raw, '歌詞：', 220, true)}
                                  </EuiFlexItem>
                                  <EuiFlexItem>
                                    <EuiFlexGroup direction="row" gutterSize="s" alignItems="center">
                                      {(r.hasOwnProperty('language')) && 
                                        <EuiText size="s">
                                          <strong>{TranslateLanguage(language,"語言: ")}</strong>
                                          <span
                                            dangerouslySetInnerHTML={{
                                              __html: `${TranslateLanguage(language, r.language.raw)}&nbsp;&nbsp;`
                                            }}
                                          />
                                        </EuiText>}
                                      {(r.hasOwnProperty('tempo')) && 
                                        <EuiText size="s">
                                          <strong>{TranslateLanguage(language,"速度: ")}</strong>
                                          <span
                                            dangerouslySetInnerHTML={{
                                              __html: `${TranslateLanguage(language, r.tempo.raw)}&nbsp;&nbsp;`
                                            }}
                                          />
                                        </EuiText>}
                                      {(r.hasOwnProperty('length')) && (
                                        <EuiText size="s">
                                          <strong>{TranslateLanguage(language,"時長: ")}</strong>
                                          <span
                                            dangerouslySetInnerHTML={{
                                              __html:
                                                TranslateLanguage(
                                                  language,
                                                  /^[0-9]{1,2}:[0-9]{2}(:[0-9]{2})?$/.test(r.length.raw)
                                                    ? r.length.raw // 如果是 hh:mm:ss 格式，直接输出
                                                    : convertDecimalToTime(r.length.raw) // 如果是小数，进行转换
                                                )
                                            }}
                                          />
                                        </EuiText>
                                      )}
                                    </EuiFlexGroup>
                                    <EuiFlexGroup direction="row" gutterSize="s" alignItems="center">
                                      {(r.hasOwnProperty('viewct')) && 
                                        <EuiText size="s">
                                          <strong>{TranslateLanguage(language,"觀看次數: ")}</strong>
                                          <span  
                                            title={r.viewct.raw} // Set the raw view count as the tooltip
                                            dangerouslySetInnerHTML={{
                                              __html: `${TranslateLanguage(language, formatViewCountCompact(r.viewct.raw))}&nbsp;&nbsp;`
                                            }}
                                          />
                                        </EuiText>}
                                      {(r.hasOwnProperty('date')) && 
                                        // <EuiText size="s">
                                        //   <strong>{TranslateLanguage(language,"日期: ")}</strong>
                                        //   <span
                                        //     dangerouslySetInnerHTML={{
                                        //       __html: `${TranslateLanguage(language, r.date.raw)}&nbsp;&nbsp;`
                                        //     }}
                                        //   />
                                        // </EuiText>
                                        <EuiText size="s">
                                          <strong>{TranslateLanguage(language, "日期: ")}</strong>
                                          <span>{formatDateYMD(r?.date?.raw) || ""}</span>
                                        </EuiText>
                                      }
                                    </EuiFlexGroup>
                                  </EuiFlexItem>
                                </EuiFlexGroup>
                                </EuiFlexItem>
                              </EuiFlexGroup>}
                            
                              {(((r.doctype.raw == "信息") ) && (r.hasOwnProperty('description'))) && 
                              <EuiFlexGroup gutterSize="m" className="sermon-search-result">
                                <EuiFlexItem grow={false} className="sermon-result-thumbnail">
                                  <EuiLink onClick={()=> showYTPreview(r.id.raw, r.link.raw, r.title.raw)}>
                                    <YTImage link={r.link.raw} />
                                  </EuiLink>
                                </EuiFlexItem>
                                <EuiFlexItem className="sermon-result-body">
                                  <div className="sermon-result-meta">
                                    {r.speaker?.raw && (
                                      <span><strong>{TranslateLanguage(language, '講員：')}</strong>{TranslateLanguage(language, r.speaker.raw)}</span>
                                    )}
                                    {r.publisher?.raw && (
                                      <span><strong>{TranslateLanguage(language, '發佈者：')}</strong>{TranslateLanguage(language, r.publisher.raw)}</span>
                                    )}
                                    {r.language?.raw && (
                                      <span><strong>{TranslateLanguage(language, '語言：')}</strong>{TranslateLanguage(language, r.language.raw)}</span>
                                    )}
                                  </div>
                                  {r.description.raw && (() => {
                                    return renderExpandableContent(`sermon:${r.id.raw}`, r.description.raw, '摘要：');
                                  })()}
                                </EuiFlexItem>
                              </EuiFlexGroup>}

                            {(((r.doctype.raw == "網路") ) && (r.hasOwnProperty('description'))) && 
                              <EuiFlexGroup gutterSize="s">
                                {(r.category.raw == "見證") && <EuiFlexItem grow={false}><EuiLink onClick={()=> showYTPreview(r.id.raw, r.link.raw, r.title.raw)}><YTImage link={r.link.raw} ></YTImage></EuiLink></EuiFlexItem>}
                                <EuiFlexItem>
                                  {renderExpandableContent(
                                    `web:${r.id.raw}`,
                                    r.category.raw == "信仰問答" ? r.description.snippet : r.description.raw,
                                    '',
                                    220
                                  )}
                                </EuiFlexItem>
                              </EuiFlexGroup>}      

                              {(((r.doctype.raw == "禱告") ) && (r.hasOwnProperty('prayer'))) && 
                              <EuiFlexGroup gutterSize="s">
                                {(r.link.raw) && <EuiFlexItem grow={false}><EuiLink onClick={()=> showYTPreview(r.id.raw, r.link.raw, r.title.raw)}><YTImage link={r.link.raw} ></YTImage></EuiLink></EuiFlexItem>}
                                <EuiFlexItem>
                                  {renderExpandableContent(`prayer:${r.id.raw}`, r.prayer.raw, '', 220)}
                                </EuiFlexItem>
                              </EuiFlexGroup>}      

                            {(((r.doctype.raw == "網路") ) && (r.hasOwnProperty('origin'))) && 
                              <EuiFlexGroup gutterSize="s">
                                <EuiFlexItem><EuiText size="s"><span dangerouslySetInnerHTML={{__html: '來源：' + TranslateLanguage(language,r.origin.raw)}} /></EuiText></EuiFlexItem>
                              </EuiFlexGroup>}                            

                            {/* {(r.hasOwnProperty('language')) && 
                              <EuiText size="s"><span dangerouslySetInnerHTML={{__html: '語言: ' + TranslateLanguage(language,r.language.raw)}} /></EuiText>} */}
                            {r.doctype.raw !== '經文' && r?.verselist?.raw && (
                              <ScriptureReferenceLinks
                                value={r.verselist.raw}
                                language={language}
                                onReferenceClick={(reference) => setIsFlyoutVisible({
                                  currentState: true,
                                  book: reference.book,
                                  chapter: String(reference.chapter),
                                  bookname: reference.book,
                                  versenumber: reference.verseRange,
                                  verse_id: '',
                                  link: '',
                                  doctype: '經文',

                                })}
                              />
                            )}

                            {/* {(r.hasOwnProperty('tempo')) && <EuiText size="s"><span  dangerouslySetInnerHTML={{__html: '速度: ' + TranslateLanguage(language,r.tempo.raw)}} /></EuiText>} */}

                            {/* {(r.hasOwnProperty('source')) && 
                              <EuiText size="s"><span  dangerouslySetInnerHTML={{__html: TranslateLanguage(language,r.source.snippet)}} /></EuiText>}      */}

                            {((r.hasOwnProperty('source')) && (r.source.snippet)) &&
                              <EuiText size="s"><span  dangerouslySetInnerHTML={{__html: TranslateLanguage(language,r.source.snippet)}} /></EuiText>}   
                            {/* ==============  Menu link items ================================ */}
                            
                            <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false} wrap={true} id={"menu_"+r.id.raw}>
                            
                              <EuiFlexItem grow={false} >
                               
                                {(r.doctype.raw == "經文") && <EuiBadge color="hollow">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "註解") && <EuiBadge color="primary">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "詩歌") && <EuiBadge color="#FEA27F">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "例證") && <EuiBadge color="#BADA55">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "拾穗") && <EuiBadge color="#FCF7BC">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "綱目") && <EuiBadge color="#DDD">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "禱告") && <EuiBadge color="accent">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "網路") && <EuiBadge color="#FF5733">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                                {(r.doctype.raw == "信息") && <EuiBadge color="#99C172">{TranslateLanguage(language,r.doctype.raw)}</EuiBadge>}
                              </EuiFlexItem>

                              {(r.hasOwnProperty('category')) && 
                              <EuiText size="s"><span  dangerouslySetInnerHTML={{__html: TranslateLanguage(language,r.category.snippet)}} /></EuiText>}    
                              {readingPlanContextActive && (
                                <EuiFlexItem grow={false}>
                                  <EuiButton
                                    iconType="plusInCircle"
                                    size="s"
                                    color="primary"
                                    isLoading={readingPlanAddingId === readingPlanResultKey(r)}
                                    isDisabled={readingPlanAddedIds.includes(readingPlanResultKey(r))}
                                    onClick={() => handleAddToReadingPlan(r)}
                                  >
                                    {TranslateLanguage(language, readingPlanAddedIds.includes(readingPlanResultKey(r)) ? '已加入' : '加入本日內容')}
                                  </EuiButton>
                                </EuiFlexItem>
                              )}


                              {(r.doctype.raw == "經文") && 
                                <EuiFlexItem grow={false}>
                                  <EuiButtonEmpty iconType="document" size="xs" color="primary" 
                                  onClick={() => setIsFlyoutVisible({currentState: true, book: r.chapter_code_name.raw, chapter: r.chapter_number.raw, bookname: r.chapter_name.raw, versenumber: r.verse_number.raw, verse_id: r.verse_id.raw, link: "", doctype: r.doctype.raw })}>{TranslateLanguage(language,"本篇經文")}</EuiButtonEmpty>
                                </EuiFlexItem>}

                              {(r.doctype.raw == "經文") && 
                                <EuiFlexItem  grow={false}>
                                  <EuiButtonEmpty iconType="branch"  size="xs" color="primary" 
                                  onClick={() => setIsFlyoutVisible({currentState: true, book: r.book_number.raw, chapter: r.chapter_number.raw, bookname: r.chapter_name.raw, versenumber: r.chapter_verse_number.raw, verse_id: r.verse_id.raw, link: "", doctype: "串珠" })}>{TranslateLanguage(language,"串珠")}</EuiButtonEmpty>
                                </EuiFlexItem>}

                              {(r.doctype.raw == "經文") && 
                                <EuiFlexItem  grow={false}>
                                  <EuiButtonEmpty iconType="tableOfContents"  size="xs" color="primary" 
                                  onClick={() => setIsFlyoutVisible({currentState: true, book: r.book_number.raw, chapter: r.chapter_number.raw, bookname: r.chapter_name.raw, versenumber: r.verse_number.raw, verse_id: r.verse_id.raw, link: "", doctype: "註解" })}>{TranslateLanguage(language,"經文註解")}</EuiButtonEmpty>
                                </EuiFlexItem>}

                              {(r.doctype.raw == "經文") && 
                                <EuiFlexItem  grow={false}>
                                  <EuiButtonEmpty iconType="heatmap"  size="xs" color="primary" 
                                  onClick={() => setIsFlyoutVisible({currentState: true, book: r.book_number.raw, chapter: r.chapter_number.raw, bookname: r.chapter_name.raw, versenumber: r.verse_number.raw, verse_id: r.verse_id.raw, link: "", doctype: "拾穗" })}>{TranslateLanguage(language,"拾穗")}</EuiButtonEmpty>
                                </EuiFlexItem>}

                              {(r.doctype.raw == "經文") && 
                                <EuiFlexItem  grow={false}>
                                  <EuiButtonEmpty iconType="inspect"  size="xs" color="primary" 
                                  onClick={() => setIsFlyoutVisible({currentState: true, book: r.book_number.raw, chapter: r.chapter_number.raw, bookname: r.chapter_name.raw, versenumber: r.verse_number.raw, verse_id: r.verse_id.raw, link: "", doctype: "例證" })}>{TranslateLanguage(language,"例證")}</EuiButtonEmpty>
                                </EuiFlexItem>}     

                              {(r.doctype.raw == "經文") && 
                                <EuiFlexItem  grow={false}>
                                  <EuiButtonEmpty iconType="list"  size="xs" color="primary" 
                                  onClick={() => setIsFlyoutVisible({currentState: true, book: r.book_number.raw, chapter: r.chapter_number.raw, bookname: r.chapter_name.raw, versenumber: r.verse_number.raw, verse_id: r.verse_id.raw, link: "", doctype: "綱目" })}>{TranslateLanguage(language,"綱目")}</EuiButtonEmpty>
                                </EuiFlexItem>}   

                              {(r.doctype.raw == "經文") && 
                                <EuiFlexItem  grow={false}>
                                  <EuiButtonEmpty iconType="documents"  size="xs" color="primary" 
                                  onClick={() => setIsFlyoutVisible({currentState: true, book: r.book_number.raw, chapter: r.chapter_number.raw, bookname: r.chapter_name.raw, versenumber: r.verse_number.raw, verse_id: r.verse_id.raw, link: "", doctype: "短篇信息" })}>{TranslateLanguage(language,"短篇信息")}</EuiButtonEmpty>
                                </EuiFlexItem>}                        

                              {(r.doctype.raw == "禱告" && (r.link.raw) == "") && 
                              <EuiFlexItem  grow={false}>
                                <EuiButtonEmpty iconType="document"  size="xs" color="primary" 
                                onClick={() => setIsFlyoutVisible({currentState: true, book: "", chapter: "", bookname: r.title.raw, versenumber: r.verselist.raw, verse_id: r.id.raw, link: "", doctype: "禱告" })}>{TranslateLanguage(language,"禱告內容")}</EuiButtonEmpty>
                              </EuiFlexItem>}

                              {((r.hasOwnProperty('link') && (r.link.raw))) && (r.doctype.raw != '網路') &&
                                <EuiFlexItem grow={false}>
                                  <EuiButtonEmpty iconType="link" size="xs" color="primary" href={getCurrentCCBibleStudyLink(r.link.raw)} target='_blank'>{TranslateLanguage(language,"連結來源")}</EuiButtonEmpty>
                               </EuiFlexItem>}

                              {((r.hasOwnProperty('link') && (r.link.raw))) && (r.doctype.raw == '網路') &&
                              <EuiFlexItem grow={false}>
                              {(r.category.raw == "信仰問答") ?
                              <EuiButtonEmpty iconType="link"  size="xs" color="primary" 
                              onClick={() => setIsFlyoutVisible({currentState: true, book: "", chapter: "", bookname: r.title.raw, versenumber: r.verselist.raw, verse_id: r.id.raw, link: "", doctype: "信仰問答" })}>{TranslateLanguage(language,"詳細內容")}</EuiButtonEmpty>
                              :<EuiButtonEmpty iconType="link" size="xs" color="primary" href={(r.hasOwnProperty('link')) && r.link.raw} target='_blank'>{TranslateLanguage(language,"連結來源")}</EuiButtonEmpty>
                              }
                              </EuiFlexItem>
                              }

                              {(r.doctype.raw == "詩歌") &&(
                                <>                     
                                  <EuiFlexItem grow={false}>
                                        <EuiButtonEmpty
                                          iconType="heart"
                                          size="xs"
                                          color="primary"
                                          onClick={() => handleAddplaylistClick(r)}
                                        >
                                          {TranslateLanguage(language, "加入歌單")}
                                        </EuiButtonEmpty>                              
                                  </EuiFlexItem>
                                  {userRole == "Admin" &&
                                    <EuiFlexItem grow={false}>
                                        <EuiButtonEmpty
                                          iconType="save"
                                          size="xs"
                                          color="primary"
                                          onClick={() => handleAddradiolistClick(r)}
                                        >
                                          {TranslateLanguage(language, "加入電臺")}
                                        </EuiButtonEmpty>                              
                                  </EuiFlexItem>}    
                                  </>
                                  )}    
                              {(r.doctype.raw == "詩歌") &&
                              
                                <EuiFlexItem  grow={false}>
                                <EuiButtonEmpty iconType="play" size="xs" color="primary" onClick={() =>handlePlayPlaylistClick()}>{TranslateLanguage(language,"播放歌單")}</EuiButtonEmpty>
                                </EuiFlexItem>
                              }    

                              {userRole === 'Admin' && (
                                <EuiFlexItem grow={false}>
                                  <EuiButtonEmpty
                                    iconType="pencil"
                                    size="xs"
                                    color="primary"
                                    onClick={() => setEditingResult({ id: r.id.raw, title: r.title.raw })}
                                  >
                                    {TranslateLanguage(language, '編輯')}
                                  </EuiButtonEmpty>
                                </EuiFlexItem>
                              )}
                              
                              <EuiFlexItem  grow={false}>
                              <EuiButtonEmpty iconType="heart" size="xs" color="primary" onClick={()=>{
                                var itemType = "t";
                                if (r.doctype.raw == "詩歌" || r.doctype.raw == "信息" ){
                                  itemType = "v";
                                }
                                else if (r.doctype.raw == "禱告" || r.doctype.raw == "網路")
                                {
                                  if (r.link.raw != ""){
                                    itemType = "v";
                                  }
                                }
                                var link = (r.hasOwnProperty('link')) ? r.link.raw : "";
                                handleAddfavoriteClick(r,link,itemType);
                                // SaveShareListItem("",1,r.id.raw, link , r.title.raw, itemType, r.doctype.raw );
                                // setIsFlyoutVisible({currentState: true, book: "", chapter: "", bookname: "", verse_id: "", link: "", doctype: "我的收藏"});
                                }}>
                                {TranslateLanguage(language,"收藏與分享")}</EuiButtonEmpty>
                              </EuiFlexItem>

                              <EuiFlexItem  grow={false}>
                                <EuiButtonEmpty iconType="storage" size="xs" color="primary" onClick={() =>handleEnterFavorlistClick()}>{TranslateLanguage(language,"打開收藏")}</EuiButtonEmpty>
                              </EuiFlexItem>
                              
                            </EuiFlexGroup>
                            {/* ==============  End of Menu link items ================================*/}

                            {r.doctype.raw === '經文' && r?.verse_id?.raw && (
                              <InlineCrossReferences
                                verseId={r.verse_id.raw}
                                language={language}
                                onReferenceClick={reference => setIsFlyoutVisible({
                                  currentState: true,
                                  book: reference.book,
                                  chapter: reference.chapter,
                                  bookname: reference.bookname,
                                  versenumber: reference.verseRange,
                                  verse_id: reference.id,
                                  link: '',
                                  doctype: '經文',
                                })}
                              />
                            )}

                            <EuiFlexGroup gutterSize="xs" alignItems="left" responsive={false} wrap={true}>
                            
                            {(r.tag?.raw) &&
                              (Array.isArray(r.tag.raw)
                                ? r.tag.raw
                                : String(r.tag.raw).split(/[,，;；]/).map((tag) => tag.trim()).filter(Boolean)
                              ).map(tagitem =>(
                                <>
                                <div key={tagitem}></div>
                                <EuiFlexItem  grow={false} >
                                  <EuiButtonEmpty size="xs" color="primary" 
                                  onClick={() => {setSearchTerm("#"+tagitem); setFilter("tag",tagitem, "any");} }>#{tagitem}</EuiButtonEmpty>
                                </EuiFlexItem>
                                </>
                              ))                          
                              } 
                            </EuiFlexGroup>

                            </div>
                          </EuiPanel>
                        </div>
                      ))}
                    </>
                  }
                  bodyHeader={
                    <React.Fragment>
                      {wasSearched && <PagingInfo />}
                      {wasSearched && <ResultsPerPage options={[10, 20, 40, 60]} />}
                    </React.Fragment>
                  }
                  bodyFooter={<Paging 
                    view={(props) =>
                      PagingView({
                        ...props,
                        // onChange: (value) => {
                        //   window.scrollTo(0, 0);
                        //   return props.onChange(value);
                        // }
                        onChange: (value) => {
                          const changeResult = props.onChange(value);
                          scrollToSearchPageTop();
                          return changeResult;
                        }
                        
                      })
                    }/>
                    }
                />
              </ErrorBoundary>
              // Landing page with no search term
              // Home page
              ) : (
                  <EuiPageTemplate 
                    template="centeredBody"
                    pagecontentprops={{ paddingSize: 's' }}
                    // style={{ minHeight: '100px', maxHeight: '95vh', overflowY: 'auto' }}
                    style={{ minHeight: '100px', maxHeight: '95vh', backgroundColor: '#FFFFFF'}}
                  >
                  <EuiEmptyPrompt title={<span><img className="responsive-logo" src={logo} alt="Check The Bible" /></span>} 
                    body={
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <SearchBox  autocompleteSuggestions={false} className = "bar"
                                    onSubmit={(e) => {
                                      // setSearchTerm(TranslateInput((document.getElementById("inputTermHome").value)));
                                      if (inputHomeRef.current) {
                                        const term = inputHomeRef.current.value;  // 使用 ref 获取输入框的值
                                        setSearchTerm(TranslateInput(term));  // 使用获取的值进行搜索
                                      }
                                    }}
                                    inputView={() => (
                                        <>
                                          <input 
                                            className="searchbar" 
                                            id="inputTermHome" 
                                            ref={inputHomeRef}
                                            defaultValue={initialSearchTerm}
                                            autoComplete="off" 
                                            placeholder={TranslateLanguage(language,"請輸入關鍵字。如馬太福音")} 
                                            autoFocus={isMobile} 
                                          />
                                          {/* {(browserSupportsSpeechRecognition || Capacitor.isNativePlatform()) && <img onClick={()=>{setIsMobile(false);showModal();}}  className="voice"  src={micIcon} title="語音搜尋" />} */}
                                          {browserSupportsSpeechRecognition && !Capacitor.isNativePlatform() && (
                                            <img onClick={()=>{setIsMobile(false);showModal();}}  className="voice"  src={micIcon} title="語音搜尋" />
                                          )}
                                        </>
                                      )}
                        /> 
                        {/* <button onClick={openBibleSelectHomeModal} style={{ cursor: 'pointer', border: 'none', background: 'none' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <EuiIcon size="l" type={bibleIcon} />
                          </div>
                        </button> */}
                      </div>
                  } 
                  />
                  <EuiFlexGroup alignItems="center" justifyContent="center" responsive={false} style={{ margin: '10px', maxHeight: '100px' }}>
                    <EuiFlexItem grow={false} style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                      <EuiButton color="text" iconType="search" className="searchbutton"
                        onClick={() => {
                          if (inputHomeRef.current) {
                            const term = inputHomeRef.current.value;  // 使用 ref 获取输入框的值
                            setSearchTerm(TranslateInput(term));  // 使用获取的值进行搜索
                          }
                        }}>
                        {TranslateLanguage(language, "搜尋")}
                      </EuiButton>
                      <button 
                          onClick={openBibleSelectHomeModal} 
                          style={{ 
                            cursor: 'pointer', 
                            border: 'none', 
                            background: 'none', 
                            marginLeft: '10px',
                          }}
                        >
                          <EuiIcon size="xxl" type={bibleIcon} /> 
                      </button>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                  {/* 添加说明文字 */}
                  <EuiFlexGroup
                    alignItems="center"
                    justifyContent="center"
                    responsive={false}
                    style={{ margin: '10px 0' }}
                  >
                    {!Capacitor.isNativePlatform() && (
                      <EuiFlexItem grow={false}>
                        <div className="store-badges-wrap">
                          <div className="store-badges-row">
                            <a
                              href="https://apps.apple.com/us/app/checkthebible/id6751795925"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="store-badge-link"
                            >
                              <img
                                src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                                alt="Download on the App Store"
                                className="store-badge-img ctb-breath"
                              />
                            </a>

                            <a
                              href="https://play.google.com/store/apps/details?id=com.checkthebible.app"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="store-badge-link"
                            >
                              <img
                                src="/GetItOnGooglePlay_Badge_Web_color_English.svg"
                                alt="Get it on Google Play"
                                className="store-badge-img ctb-breath"
                              />
                            </a>
                          </div>
                        </div>
                      </EuiFlexItem>
                    )}

                    <style>
                      {`
                        @keyframes breathe {
                          0% {
                            transform: scale(1);
                            opacity: 0.94;
                          }
                          25% {
                            transform: scale(1.045);
                            opacity: 1;
                          }
                          50% {
                            transform: scale(1);
                            opacity: 0.94;
                          }
                          100% {
                            transform: scale(1);
                            opacity: 0.94;
                          }
                        }

                        .store-badges-wrap {
                          width: 100%;
                          display: flex;
                          justify-content: center;
                          align-items: center;
                        }

                        .store-badges-row {
                          display: flex;
                          flex-direction: row;
                          justify-content: center;
                          align-items: center;
                          flex-wrap: nowrap;
                          white-space: nowrap;
                          gap: 18px;
                        }

                        .store-badge-link {
                          width: 156px;              /* 完全等宽 */
                          height: 48px;              /* 完全等高 */
                          display: inline-flex;
                          align-items: center;
                          justify-content: center;
                          flex: 0 0 auto;
                          text-decoration: none;
                          border-radius: 12px;
                          padding: 4px;
                          box-sizing: border-box;
                          overflow: visible;
                        }

                        .store-badge-img {
                          width: 100%;
                          height: 100%;
                          object-fit: contain;       /* 保持官方 badge 比例 */
                          display: block;
                        }

                        .ctb-breath {
                          animation: breathe 2.6s ease-in-out infinite;
                          transform-origin: center center;
                          will-change: transform, opacity;
                        }

                        .store-badge-link:nth-child(1) .ctb-breath {
                          animation-delay: 0s;
                        }

                        .store-badge-link:nth-child(2) .ctb-breath {
                          animation-delay: 1.3s;     /* 交替呼吸 */
                        }

                        .store-badge-link:hover .store-badge-img {
                          animation-play-state: paused;
                          transform: scale(1.03);
                          transition: transform 0.2s ease;
                        }

                        @media (max-width: 600px) {
                          .store-badges-row {
                            gap: 14px;
                          }

                          .store-badge-link {
                            width: 132px;
                            height: 42px;
                          }
                        }

                        @media (max-width: 380px) {
                          .store-badges-row {
                            gap: 10px;
                          }

                          .store-badge-link {
                            width: 118px;
                            height: 38px;
                          }
                        }
                      `}
                    </style>
                  </EuiFlexGroup>

                    {/* <EuiFlexGroup alignItems="center" responsive={false}>
                      <EuiFlexItem>
                                 
                      </EuiFlexItem>
                    </EuiFlexGroup> */}
                <DailyWidget></DailyWidget> 

                </EuiPageTemplate>   
              )
              // End of Home Page
             } 
              {/* 彈出模態窗口 */}
              <BibleSelectorModal
                isVisible={isModalVisible}
                onClose={closeBibleSelectModal}
                onSelect={(verse) => handleSelectVerse(verse, setSearchTerm, setFilter, removeFilter, false, (v) => v)}
                /> 
              <BibleSelectorModal
                isVisible={isModalHomeVisible}
                onClose={closeBibleSelectHomeModal}
                onSelect={(verse) => handleSelectVerseHome(verse, setSearchTerm, setFilter, removeFilter, false, (v) => v)}
                /> 
              <ChatBotModal isVisible={isChatBotVisible} closeModal={closeChatBot} />
              {flyout}
              {ytPreview}
              {/* 渲染 PlaylistModal */}
              {showPlaylistModal && (
                <PlaylistModal
                  userId={modalProps.userId}
                  songId={modalProps.songId}
                  videoURL={modalProps.videoURL}
                  title={modalProps.title}
                  type={modalProps.type}
                  engineId={modalProps.engineId}
                  label={modalProps.label}
                  playlisttype={modalProps.playlisttype}
                  closeModal={() => setShowModal(false)}
                />
              )}
              {/* 渲染 FavoriteModal */}
              {showFavoriteModal && (
                <FavoriteModal
                  userId={modalProps.userId}
                  songId={modalProps.songId}
                  videoURL={modalProps.videoURL}
                  title={modalProps.title}
                  type={modalProps.type}
                  engineId={modalProps.engineId}
                  label={modalProps.label}
                  playlisttype={modalProps.playlisttype}
                  resource={modalProps.resource}
                  closeModal={() => setShowFavoriteModal(false)}
                />
              )}
              <LoginRequiredModal
                  isOpen={showLoginModal}
                  onClose={() => setShowLoginModal(false)}
                  nextUrl={typeof window !== 'undefined' ? window.location.href : '/'}
                  title={TranslateLanguage(localStorage.userLanguage, "請先登入")}
                  message={TranslateLanguage(localStorage.userLanguage, "登入後即可使用此功能，若還不是用戶請注冊用戶。")}
              />
              {userRole === 'Admin' && editingResult && (
                <SearchResultEditor
                  scopedId={editingResult.id}
                  title={editingResult.title}
                  token={token}
                  language={language}
                  onClose={() => setEditingResult(null)}
                  onDeleted={id => {
                    setDeletedResultIds(current => current.includes(id) ? current : [...current, id]);
                    setSearchTerm(resultSearchTerm, { shouldClearFilters: false, refresh: true });
                  }}
                />
              )}
            </div>
            
          );
        }}
      </WithSearch>
      {/* <Footer withLanguage="1" /> */}
      </div>
    </SearchProvider> 
  );
}
